import React, { forwardRef, useEffect, useId, useRef, useState } from "react";
import type { ComponentPropsWithoutRef, ReactNode } from "react";
import styles from "./optionBlock.module.css";
import Tooltip from "@mui/material/Tooltip";
import { Typography } from "@mui/material";
import { CoreChip, CoreChipProps, CoreInfoIndicator, CoreRadioCardIcon, CoreSwitch } from "../../core";
import { useMobileLayout } from "../../hooks";
import { ProctoringPlatforms } from "@verity/proctoring-option/types";
import i18nPackage from "../../i18n/i18n";
import {
	GROUPED_OPTION_TEXT,
	isGroupedOptionKey,
	PROCTORING_OPTION_TEXT,
	RenderableProctoringKey,
} from "./option-text/ProctoringOptionText";
import { useProctoringOptionContext } from "../context/EditQuizContext";
import { useProctoringHelpers } from "./hooks/useProctoringHelpers";

export type OptionBlockProps = {
	optionKey: RenderableProctoringKey;

	/**Overrides */
	description?: ReactNode;
	/**Overrides */
	contentHeader?: string;
	/**Overrides */
	Icon?: React.ElementType;

	/**Overrides @default true */
	showCheckmark?: boolean;

	/**Overrides. Default: !isOptionAllowed(key) || enforcedProfilesOnly */
	disabled?: boolean;
	disabledMessage?: React.ReactNode;

	/**Renders a info icon next to the header */
	hint?: React.ReactNode;

	checked: boolean;
	updateState?: (state: boolean) => void;
	/**Will be aligned with the description */
	menuItems?: React.ReactNode;
	/** The width of the toggle under which the icon will be hidden. */
	compactWidth?: number;

	/**Overrides. Default:  getPartiallySupportedPlatforms(optionKey)*/
	partiallySupportedPlatforms?: Set<keyof ProctoringPlatforms>;
	/**Aligned with the icon. Takes up full width of option block */
	children?: React.ReactNode;

	ariaHasPopup?: boolean | "dialog" | "menu" | "grid" | "true" | "false" | "listbox" | "tree" | undefined;

	/** Extra props for the option's outer card, e.g. to mark it as a scroll or highlight target. */
	containerProps?: Omit<ComponentPropsWithoutRef<"div">, "children">;
};

function OptionBlockTooltip({ tooltip }: { tooltip: React.ReactNode }) {
	return (
		<Tooltip
			title={
				<div className={styles.infoTextContainer}>
					{typeof tooltip === "string" ? <Typography className={styles.infoText}>{tooltip}</Typography> : tooltip}
				</div>
			}
			PopperProps={{
				modifiers: [
					{
						name: "arrow",
						options: {
							padding: 0,
						},
					},
					{
						name: "offset",
						options: {
							offset: [0, 10],
						},
					},
					{
						name: "preventOverflow",
						options: {
							padding: 8,
						},
					},
					{
						name: "computeStyles",
						options: {
							adaptive: false,
						},
					},
					{
						name: "arrowCenterFix",
						enabled: true,
						phase: "write",
						fn({ state }) {
							const arrow = state.elements.arrow;
							if (arrow) {
								arrow.style.left = "50%";
								arrow.style.transform = "translateX(-50%)";
							}
						},
					},
				],
			}}
		>
			<div
				style={{
					top: 0,
					left: 0,
					position: "absolute",
					height: "80%",
					width: "100%",
					zIndex: 0,
				}}
			/>
		</Tooltip>
	);
}

const OptionBlockContainer = forwardRef<
	HTMLDivElement,
	Pick<OptionBlockProps, "disabled" | "disabledMessage" | "compactWidth" | "containerProps"> & {
		children: ReactNode;
	}
>(function OptionBlockContainer({ disabled, disabledMessage, compactWidth, containerProps, children }, ref) {
	const [compacted, setCompacted] = useState(false);
	const wrapperRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		if (!wrapperRef.current || compactWidth === undefined) {
			return;
		}

		const resizeObserver = new ResizeObserver(() => {
			setTimeout(() => {
				if (wrapperRef.current?.clientWidth && wrapperRef.current.clientWidth < compactWidth) {
					if (!compacted) {
						setCompacted(true);
					}
				} else if (compacted) {
					setCompacted(false);
				}
			}, 0);
		});
		resizeObserver.observe(wrapperRef.current);
		return function cleanup() {
			resizeObserver.disconnect();
		};
	}, [compacted, compactWidth]);

	return (
		<div
			{...containerProps}
			className={`${styles.optionBoxWrapper} ${containerProps?.className ?? ""}`}
			ref={(node) => {
				wrapperRef.current = node;
				if (typeof ref === "function") ref(node);
				else if (ref) ref.current = node;
			}}
		>
			{children}
			{disabled && disabledMessage && <OptionBlockTooltip tooltip={disabledMessage} />}
		</div>
	);
});

function OptionBlockDescription({ optionKey, description }: Pick<OptionBlockProps, "description" | "optionKey">) {
	const isGroupedKey = isGroupedOptionKey(optionKey);

	const textConfig = isGroupedKey ? GROUPED_OPTION_TEXT[optionKey] : PROCTORING_OPTION_TEXT[optionKey];
	const finalDescription = description ?? textConfig.description;
	return <Typography className={styles.optionDescriptionText}>{finalDescription}</Typography>;
}

function OptionBlockInfo({
	contentHeader,
	description,
	Icon,
	disabled,
	id,
	partiallySupportedPlatforms,
	optionKey,
	checked,
	hint,
	showCheckmark = true,
}: Pick<
	OptionBlockProps,
	| "contentHeader"
	| "description"
	| "Icon"
	| "disabled"
	| "partiallySupportedPlatforms"
	| "optionKey"
	| "checked"
	| "hint"
	| "showCheckmark"
> & {
	id?: string;
}) {
	const isGroupedKey = isGroupedOptionKey(optionKey);
	const { isMobileLayout } = useProctoringOptionContext();
	const { getPartiallySupportedPlatforms } = useProctoringHelpers();

	const textConfig = isGroupedKey ? GROUPED_OPTION_TEXT[optionKey] : PROCTORING_OPTION_TEXT[optionKey];

	const header = contentHeader ?? textConfig.header;
	const DefaultIcon = textConfig.icon;

	const supportedPlatforms =
		partiallySupportedPlatforms ?? (isGroupedKey ? undefined : getPartiallySupportedPlatforms(optionKey));

	return (
		<>
			<div className={styles.iconContainer}>
				{Icon && <Icon aria-hidden="true" />}
				{DefaultIcon && !Icon && (
					<CoreRadioCardIcon
						enabled={checked && !disabled}
						icon={<DefaultIcon aria-hidden="true" />}
						showCheckmark={showCheckmark}
					/>
				)}
			</div>
			<div className={styles.optionBlockCol}>
				<div className={`${styles.optionTitleWrapper} ${disabled ? styles.disabled : ""}`}>
					<Typography variant="h3" className={styles.optionTitleText} id={id}>
						{header}
					</Typography>
					{hint && <CoreInfoIndicator hint={hint} size={20} />}
					{Array.from(supportedPlatforms ?? []).map((platform) => (
						<OptionBlockPlatformBadge key={platform} platform={platform} />
					))}
				</div>
				{!isMobileLayout && <OptionBlockDescription description={description} optionKey={optionKey} />}
			</div>
		</>
	);
}

export function OptionBlockPlatformBadge({
	platform,
	chipProps,
}: {
	platform: keyof ProctoringPlatforms;
	chipProps?: Omit<CoreChipProps, "chipColor" | "children">;
}) {
	const badgeTextKeyByPlatform = {
		isExtension: "extension",
		isLockdown: "lockdown",
		isMobile: "mobile",
		isChromeOs: "chromeOs",
		isBrowser: "browser",
	} as const;

	const chipColorByPlatform = {
		isExtension: "#F4ECFA",
		isLockdown: "#EAF6F6",
		isMobile: "#DFF2F2",
		isChromeOs: "#F5E5F4",
		isBrowser: "#E8F0FE",
	} as const;

	const badgeTextKey = badgeTextKeyByPlatform[platform];
	const chipColor = chipColorByPlatform[platform];
	return (
		<CoreChip
			chipColor={chipColor}
			variant="rectangular"
			className={styles.platformBadge}
			tooltip={i18nPackage.t(`editQuizPage.proctoringOptionsTextTSX.badge.${badgeTextKey}.tooltip`)}
			{...chipProps}
		>
			{i18nPackage.t(`editQuizPage.proctoringOptionsTextTSX.badge.${badgeTextKey}.badge`)}
		</CoreChip>
	);
}

const OptionBlock = forwardRef<HTMLDivElement, OptionBlockProps>(function OptionBlock(
	{
		description,
		contentHeader,
		Icon,
		disabled,
		disabledMessage,
		checked,
		menuItems,
		compactWidth = 225,
		updateState,
		partiallySupportedPlatforms,
		optionKey,
		children,
		hint,
		ariaHasPopup,
		showCheckmark,
		containerProps,
	},
	ref,
) {
	const headerId = useId();
	const isMobileLayout = useMobileLayout();

	const { enforcedProfilesOnly } = useProctoringOptionContext();
	const { isOptionAllowed } = useProctoringHelpers();

	// Disabled if enforcedProfile is true, otherwise check isOptionAllowed if it is a proctoring key
	const finalDisabled =
		disabled ?? (enforcedProfilesOnly || (!isGroupedOptionKey(optionKey) && !isOptionAllowed(optionKey)));

	return (
		<OptionBlockContainer
			disabled={finalDisabled}
			disabledMessage={disabledMessage}
			compactWidth={compactWidth}
			containerProps={containerProps}
			ref={ref}
		>
			<div className={styles.optionBlock}>
				<OptionBlockInfo
					contentHeader={contentHeader}
					description={description}
					Icon={Icon}
					disabled={finalDisabled}
					id={headerId}
					partiallySupportedPlatforms={partiallySupportedPlatforms}
					optionKey={optionKey}
					checked={checked}
					hint={hint}
					showCheckmark={showCheckmark}
				/>
				{updateState && (
					<CoreSwitch
						checked={checked}
						onChange={(event) => {
							const newChecked = event.target.checked;
							if (!finalDisabled) {
								updateState(newChecked);
							}
						}}
						disabled={finalDisabled && !checked}
						inputProps={{ "aria-labelledby": headerId }}
						labelTextType={isMobileLayout ? "none" : "left"}
						aria-haspopup={ariaHasPopup ?? undefined}
					/>
				)}
				{isMobileLayout && <OptionBlockDescription description={description} optionKey={optionKey} />}
				{checked && menuItems && <div className={styles.menuItemsWrapper}>{menuItems}</div>}
			</div>
			{children}
		</OptionBlockContainer>
	);
});

export default OptionBlock;
